import { motion } from "framer-motion";
import { Users, Video, User, ArrowRight, Check } from "lucide-react";

const CARDS = [
  {
    value: "gruppo_presenza",
    capKey: "gruppo_presenza",
    icon: Users,
    tag: "In presenza · Lugano Paradiso",
    title: "Gruppo in presenza",
    badge: "Cicli 2027 aperti",
    desc: "Il cuore di Arcipelago: sei incontri mensili in un gruppo aperto di adulti con ADHD, dove psicoeducazione e auto-aiuto si intrecciano.",
    points: [
      "6 incontri, un mercoledì al mese, 17:00 – 18:30",
      "Confronto e crescita condivisa in un clima protetto",
      "Posti limitati per garantire ascolto a tutti",
    ],
    testId: "percorso-card-gruppo-presenza",
    btnTestId: "percorso-btn-gruppo-presenza",
  },
  {
    value: "gruppo_online",
    capKey: "gruppo_online",
    icon: Video,
    tag: "Online · Giovedì 20:00 – 22:00",
    title: "Gruppo online",
    badge: "Max 5 per ciclo",
    desc: "Un gruppo ristretto in videochiamata il giovedì sera: due cicli 2027 tra cui scegliere, per uno spazio digitale intimo e su misura.",
    points: [
      "6 incontri, un giovedì al mese, 20:00 – 22:00",
      "Due cicli 2027: Gennaio–Giugno e Luglio–Dicembre",
      "Massimo 5 partecipanti per ciclo",
    ],
    testId: "percorso-card-gruppo-online",
    btnTestId: "percorso-btn-gruppo-online",
  },
  {
    value: "individuale",
    icon: User,
    tag: "Una persona · In presenza o online",
    title: "Percorso individuale",
    badge: "Iscrizione sempre aperta",
    desc: "Uno spazio tutto tuo: si inizia con la psicoeducazione sui 6 temi dell'ADHD, poi decidi in autonomia se concludere o prolungare il percorso.",
    points: [
      "6 incontri da 75 minuti, date concordate insieme",
      "In presenza a Lugano Paradiso oppure online",
      "Iscrizione sempre aperta: inizi quando vuoi",
    ],
    testId: "percorso-card-individuale",
    btnTestId: "percorso-btn-individuale",
  },
];

const PostiChip = ({ info, testId }) => {
  if (!info) return null;
  const esauriti = info.rimasti <= 0;
  const pochi = !esauriti && info.rimasti <= 2;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold ${
        esauriti
          ? "bg-[#C93B2B]/10 text-[#C93B2B]"
          : pochi
            ? "bg-amber-500/10 text-amber-700"
            : "bg-[#0D9488]/10 text-[#0D9488]"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${esauriti ? "bg-[#C93B2B]" : pochi ? "bg-amber-500" : "bg-[#0D9488]"}`} />
      {esauriti ? "Posti esauriti" : `${info.rimasti} posti rimasti su ${info.capienza}`}
    </span>
  );
};

const Percorsi = ({ selected, onSelect, posti, onOpenModal }) => (
  <section id="percorsi" className="relative bg-[#FAF8F5] py-20 sm:py-28" data-testid="percorsi-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-14 max-w-2xl"
      >
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.22em] text-[#0D9488]">01 — Il percorso</p>
        <h2 className="font-display text-3xl font-semibold tracking-tight text-[#0C1B33] sm:text-5xl">
          Scegli come navigare
        </h2>
        <p className="mt-4 text-base leading-relaxed text-[#475569] sm:text-lg">
          Tre rotte, una destinazione: capire il proprio ADHD e imparare a conviverci bene.
          Seleziona la modalità che senti più tua — potrai confermarla nel modulo di iscrizione.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {CARDS.map((card, idx) => {
          const Icon = card.icon;
          const isSel = selected === card.value;
          return (
            <motion.article
              key={card.value}
              data-testid={card.testId}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: idx * 0.12 }}
              whileHover={{ y: -6 }}
              className={`group relative flex flex-col rounded-2xl border bg-[#FFFDF9] p-7 transition-[border-color,box-shadow] duration-300 sm:p-8 ${
                isSel
                  ? "border-[#0D9488] shadow-[0_18px_44px_rgba(13,148,136,0.18)]"
                  : "border-[#E2D9CD] shadow-[0_6px_20px_rgba(12,27,51,0.05)] hover:shadow-[0_16px_40px_rgba(12,27,51,0.1)]"
              }`}
            >
              <span className="absolute right-6 top-6 rounded-full bg-[#0D9488]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#0D9488]">
                {card.badge}
              </span>

              <div
                className={`mb-6 flex h-14 w-14 items-center justify-center rounded-full transition-colors duration-300 ${
                  isSel ? "bg-[#0D9488] text-white" : "bg-[#0C1B33]/5 text-[#0C1B33] group-hover:bg-[#0D9488]/15 group-hover:text-[#0D9488]"
                }`}
              >
                <Icon className="h-6 w-6" />
              </div>

              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#0D9488]">{card.tag}</p>
              <h3 className="mt-2 font-display text-2xl font-semibold text-[#0C1B33]">{card.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#475569]">{card.desc}</p>

              <ul className="mt-5 flex-1 space-y-2.5">
                {card.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-[#334155]">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0D9488]" />
                    {p}
                  </li>
                ))}
              </ul>

              {card.capKey && (
                <div className="mt-5">
                  <PostiChip info={posti?.[card.capKey]} testId={`posti-rimanenti-${card.value}`} />
                </div>
              )}

              {(() => {
                const esaurito = Boolean(card.capKey && posti?.[card.capKey] && posti[card.capKey].rimasti <= 0);
                return (
                  <button
                    data-testid={card.btnTestId}
                    onClick={() => onOpenModal(card.value)}
                    className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-[transform,background-color,color,border-color] duration-300 ${
                      esaurito
                        ? "border border-amber-400/70 bg-amber-50 text-amber-800 hover:bg-amber-500 hover:text-white"
                        : isSel
                          ? "bg-[#0D9488] text-white"
                          : "border border-[#0C1B33]/25 text-[#0C1B33] hover:border-[#0D9488] hover:bg-[#0D9488] hover:text-white"
                    }`}
                  >
                    {esaurito ? "Iscriviti alla lista d'attesa" : "Scegli questo percorso"}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                );
              })()}
            </motion.article>
          );
        })}
      </div>
    </div>
  </section>
);

export default Percorsi;
